/**
 * talkTrack.ts — Logica de notas consultivas para o Modo Call (PresentationMode).
 *
 * Este modulo e testavel de forma independente do Svelte.
 * Conteudo em pt-br. Nenhuma promessa garantida de resultado.
 */

import type { ReportSectionKind } from '../../core/report/report.types';
import type { Recommendation } from '../../core/recommendations/recommendation.types';
import type { DiagnosticMetrics } from '../../core/metrics/metrics.types';

export interface TalkTrackNote {
  /** Texto curto para o consultor falar. */
  text: string;
  /** Categoria da nota (abertura, pergunta, transicao, fechamento, limite). */
  kind: 'abertura' | 'pergunta' | 'transicao' | 'limite' | 'fechamento';
}

export interface NextStepOption {
  label: string;
  description: string;
  module: Recommendation['module'];
}

const TALK_TRACK: Record<ReportSectionKind, TalkTrackNote[]> = {
  cover: [
    {
      text: 'Apresente o objetivo do diagnostico e o periodo analisado. Reforce que e um indicador tecnico, nao auditoria.',
      kind: 'abertura',
    },
    {
      text: 'Diga: "Vamos explorar o que os arquivos de ponto revelam — sem enviar nenhum dado para fora daqui."',
      kind: 'abertura',
    },
    {
      text: 'Pergunte: "Antes de comecar, tem alguma dor especifica que voce gostaria que a gente olhasse com mais atencao?"',
      kind: 'pergunta',
    },
    {
      text: 'Transite para o resumo executivo apos ouvir a resposta. Guarde o que o cliente disse como fio condutor.',
      kind: 'transicao',
    },
  ],
  'executive-summary': [
    {
      text: 'Comece pelos 5 numeros executivos. Nao explique ainda — mostre primeiro.',
      kind: 'abertura',
    },
    {
      text: 'Pergunte: "Esses numeros fazem sentido com o que voce ve no dia a dia?" Deixe o cliente reagir antes de explicar.',
      kind: 'pergunta',
    },
    {
      text: 'Se o cliente discordar de algum numero, anote. Pode ser premissa revisavel ou particularidade do negocio.',
      kind: 'limite',
    },
    {
      text: 'Use frases como "indicio", "padrao atipico" e "merece verificacao". Evite afirmacoes categoricas ou termos juridicos.',
      kind: 'transicao',
    },
  ],
  base: [
    {
      text: 'Explique a base de dados sem entrar em tecnicalidades. Diga "X colaboradores, Y dias, Z marcacoes".',
      kind: 'abertura',
    },
    {
      text: 'Reforce que tudo foi processado localmente: "Seus arquivos nao sairao daqui durante toda essa analise."',
      kind: 'abertura',
    },
    {
      text: 'Pergunte: "Esse periodo e representativo para o negocio de voces, ou teve alguma sazonalidade forte?"',
      kind: 'pergunta',
    },
    {
      text: 'Transite para as metricas destacando 2 ou 3 categorias mais relevantes para a dor declarada.',
      kind: 'transicao',
    },
  ],
  metrics: [
    {
      text: 'Nao passe metrica por metrica. Pegue 2 ou 3 que dialogam com a dor declarada do cliente.',
      kind: 'abertura',
    },
    {
      text: 'Mostre o impacto financeiro potencial com clareza. Use faixas ("entre X e Y") para nao prometer numeros exatos.',
      kind: 'limite',
    },
    {
      text: 'Conecte o dado de ausencia ao impacto operacional. Pergunte se o cliente reconhece o padrao.',
      kind: 'pergunta',
    },
    {
      text: 'Retrabalho do DP e dor real — valide se o time de RH reconhece o volume. Nao exagere nos dados.',
      kind: 'limite',
    },
    {
      text: 'Aborde riscos com seriedade, mas sem alarmismo. Foque no que e acionavel agora.',
      kind: 'transicao',
    },
  ],
  score: [
    {
      text: 'O score e piloto. Use como referencia, nao como nota final. Reforce que e diagnostico exploratório.',
      kind: 'abertura',
    },
    {
      text: 'Foque na BANDA (critico / atencao / consistente / exemplar), nao no numero exato.',
      kind: 'abertura',
    },
    {
      text: 'Pergunte: "Faz sentido essa classificacao comparada com o que voce vivencia internamente?"',
      kind: 'pergunta',
    },
    {
      text: 'Explique o que moveria o cliente para a proxima banda — isso abre a conversa de solucao.',
      kind: 'transicao',
    },
  ],
  recommendations: [
    {
      text: 'Conecte cada recomendacao a um modulo Pontotel. Diga o que aquele modulo resolve, nao a feature crua.',
      kind: 'abertura',
    },
    {
      text: 'Priorize as recomendacoes de maior impacto para a dor declarada, nao a lista completa.',
      kind: 'abertura',
    },
    {
      text: 'Pergunte: "Dessas recomendacoes, qual voce acha que teria mais impacto imediato no seu time?"',
      kind: 'pergunta',
    },
    {
      text: 'Transite para os proximos passos conectando a recomendacao escolhida a uma acao concreta.',
      kind: 'transicao',
    },
  ],
  limitations: [
    {
      text: 'Seja transparente sobre limitacoes. Cliente confia mais em quem assume o que nao sabe.',
      kind: 'abertura',
    },
    {
      text: 'Cite limitacoes proativamente — nao espere o cliente questionar.',
      kind: 'limite',
    },
    {
      text: 'Diga: "Essas limitacoes nao invalidam o diagnostico — elas mostram onde precisamos de mais dados para ser mais precisos."',
      kind: 'limite',
    },
  ],
  'next-steps': [
    {
      text: 'Combine 1 ou 2 proximos passos concretos. Nao saia da call sem um proximo passo agendado.',
      kind: 'fechamento',
    },
    {
      text: 'Pergunte: "Quem mais precisa ver esse diagnostico para a decisao avancar?"',
      kind: 'pergunta',
    },
    {
      text: 'Sugira datas e formatos especificos: "Que tal uma call de 30 min com o DP na proxima semana?"',
      kind: 'fechamento',
    },
    {
      text: 'Encerre reforçando: "Esse diagnostico e o ponto de partida — o proximo passo e refinar com os dados reais de voces."',
      kind: 'fechamento',
    },
  ],
  assumptions: [
    {
      text: 'Mostre as premissas para evitar a conversa "esses numeros nao batem". Premissas sao revisaveis.',
      kind: 'abertura',
    },
    {
      text: 'Diga: "Esses numeros podem ser ajustados se as premissas estiverem fora do real — custo medio, tolerancias, etc."',
      kind: 'limite',
    },
    {
      text: 'Pergunte: "O custo medio que usamos esta proximo da realidade de voces?"',
      kind: 'pergunta',
    },
  ],
};

/**
 * Retorna talk-track para uma secao do relatorio.
 * Cada secao tem 2-4 notas estruturadas por kind.
 */
export function getTalkTrackForSection(sectionId: ReportSectionKind): TalkTrackNote[] {
  return TALK_TRACK[sectionId] ?? [];
}

/**
 * Sugere perguntas consultivas baseadas em padroes nas metricas.
 * Sempre inclui ao menos 1-2 perguntas abertas independente dos valores.
 */
export function getSuggestedQuestions(metrics: DiagnosticMetrics): string[] {
  const questions: string[] = [];

  // Heuristicas baseadas em metricas
  if (
    metrics.cost.estimatedExcessCost !== null &&
    metrics.cost.estimatedExcessCost.value > 5000
  ) {
    questions.push('Como voces acompanham horas extras hoje?');
  }

  if (metrics.rework.adjustmentRate.value > 30) {
    questions.push('Quem aprova as correcoes de ponto? Tem rito definido para isso?');
  }

  if (metrics.risk.excessiveJourneys.length > 0) {
    questions.push('Voces tem controle sobre jornadas acima do limite legal?');
  }

  if (metrics.absence.faltas.value > 5) {
    questions.push('Como voces conduzem o retorno de quem teve ausencia nao justificada?');
  }

  // Perguntas abertas sempre presentes
  questions.push('Que dor essa analise fez voce lembrar?');
  questions.push('Se voce pudesse resolver apenas uma coisa mostrada aqui, qual seria?');

  return questions;
}

/**
 * Converte recomendacoes em opcoes de proximo passo concretas.
 * Considera apenas recomendacoes com priority >= medium.
 */
export function getNextStepOptions(recommendations: Recommendation[]): NextStepOption[] {
  const PRIORITY_ORDER: Record<Recommendation['priority'], number> = {
    critical: 0,
    high: 1,
    medium: 2,
    low: 3,
  };

  return recommendations
    .filter((r) => PRIORITY_ORDER[r.priority] <= PRIORITY_ORDER['medium'])
    .map((r) => ({
      label: r.nextStep,
      description: r.pain,
      module: r.module,
    }));
}
